/**
 * 时间戳的**唯一**格式化入口——全前端一张表的时间列都走它。
 *
 * ## 规范形态：`YYYY-MM-DD HH:MM:SS`（读者本地时区）
 *
 * ### 为什么是固定形态，不是 `Intl` 的 locale 形态
 *
 * 这棵树里同一个时间戳曾经有过**三种**形态：
 *
 * | 位置 | 形态 | 问题 |
 * |---|---|---|
 * | 本文件（旧） | `toLocaleString(undefined, …)` | 跟**浏览器语言**走，不跟界面语言走 |
 * | `redeems/columns.tsx`（旧） | `Intl.DateTimeFormat(i18n.language, {dateStyle:'short'})` | 两位年份（`26/10/26`） |
 * | `steamOrderRedeem/columns.tsx`（旧） | 手工 `+8h` 后截 ISO | **时区写死 UTC+8** |
 *
 * 第三条是真错：后端存的是 `utcnow()`（**带时区**的 ISO，见 `backend/src/**` 的
 * `.isoformat()`）⇒ 手工加 8 小时得到的是「UTC+8 的墙钟」，与同一批记录在兑换
 * 工作台里的显示**对不上**（工作台走浏览器本地时区）⇒ **同一个兑换事件，两屏显示
 * 两个时刻**。核账时这是硬伤。
 *
 * ### 为什么不用 locale 形态
 *
 * - **宽度稳定**：locale 形态在 `en-US` 下是 `10/26/2026, 05:45:00 PM`（23 字符），
 *   在 `zh-CN` 下是 `2026/10/26 17:45:00`（19 字符）⇒ 同一列宽在两种界面下差一截，
 *   折行 / `ellipsis` 的行为也跟着变。
 * - **可排序**：定宽形态扫一眼就能比先后（核账按时间排序是常用动作）。
 * - **不跟浏览器语言走**：界面语言是用户在本应用里选的（中 / 英），日期跟着浏览器
 *   走会出现「界面是中文、日期是英文」这种割裂。
 *
 * ### 时区
 *
 * 用**读者本地时区**（`getHours()` 这类本地取值器）⇒ 一条记录在任何时区的读者那里
 * 都是**同一个时刻**的不同墙钟表示，且与后端存的 UTC 瞬间一致。
 */

/** 两位补零（`7` → `07`）。 */
function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * 表格 / 详情里的时间戳。
 *
 * - 空值（`null` / `undefined` / `''`）⇒ `—`（与全树的空位口径一致）；
 * - 解析不了（坏串）⇒ **原样回显**（不吞掉、不猜）。
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
  return `${day} ${time}`;
}

export type RelativeSlot =
  | { key: 'now' }
  | { key: 'minutes'; count: number }
  | { key: 'hours'; count: number }
  | { key: 'days'; count: number }
  | null;

export function relativeSlot(iso: string, now: number = Date.now()): RelativeSlot {
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return null;
  const diffSeconds = Math.max(0, Math.floor((now - time) / 1000));
  if (diffSeconds < 60) return { key: 'now' };
  if (diffSeconds < 3600) return { key: 'minutes', count: Math.floor(diffSeconds / 60) };
  if (diffSeconds < 86_400) return { key: 'hours', count: Math.floor(diffSeconds / 3600) };
  const days = Math.floor(diffSeconds / 86_400);
  if (days <= 7) return { key: 'days', count: days };
  return null;
}
